import { designTokens } from "@itandi/itandi-bb-ui";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormRow } from "@itandi/itandi-bb-ui/FormRow";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputRadio } from "@itandi/itandi-bb-ui/InputRadio";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import type { ComponentProps } from "react";
import React, { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { MANUAL_INPUT_CONSTRUCTION_ID } from "~/constants/DealConstructionConst";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import type { DealConstructionEdit } from "~/models/DealConstruction";
import {
	buildResetConstruction,
	ESTIMATION_TYPE
} from "~/models/DealConstruction";
import type { DealConstructionItem } from "~/models/DealConstructionItem";

type Props = {
	dealConstructionItems: DealConstructionItem[];
	disabled: boolean;
	docType: "estimation" | "billing";
};

const MANUAL_INPUT_PREFIX = "manual-input-";

const normalizeString = (str: string): string =>
	str.normalize("NFKC").toLowerCase().trim();

export const ConstructionForm: React.FC<Props> = ({
	dealConstructionItems,
	disabled,
	docType
}: Props) => {
	const { register, formState, getValues, reset, setValue, watch } =
		useFormContext<DealConstructionEdit>();
	const { errors } = formState;
	const dealConstructionItem = watch("dealConstructionItem");
	const companyChargePer = watch("companyChargePer");
	const isManualInput =
		dealConstructionItem?.id === MANUAL_INPUT_CONSTRUCTION_ID;

	const { match: isPc } = useMediaQuery(MEDIA_QUERY.pc);

	const getOptions = useCallback(
		(inputValue: string) => {
			const options: ComponentProps<typeof SingleComboBox>["options"] =
				dealConstructionItems.map((item: DealConstructionItem) => ({
					label: item.name,
					value: String(item.id)
				}));

			// 入力テキストがあり、既存の選択肢に一致しない場合は「直接入力」オプションを追加
			if (inputValue.trim() !== "") {
				const hasExactMatch = dealConstructionItems.some(
					(item) => normalizeString(item.name) === normalizeString(inputValue)
				);
				if (!hasExactMatch) {
					options.push({
						label: `「${inputValue}」を使用`,
						value: `${MANUAL_INPUT_PREFIX}${inputValue}`
					});
				}
			}

			return options;
		},
		[dealConstructionItems]
	);

	const [constructionItemOptions, setConstructionItemOptions] = React.useState<
		ComponentProps<typeof SingleComboBox>["options"]
	>(() => getOptions(""));

	const selectedConstructionItemValue = React.useMemo(() => {
		if (!dealConstructionItem) {
			// 未選択
			return null;
		}

		// 直接入力の場合
		if (dealConstructionItem.id === MANUAL_INPUT_CONSTRUCTION_ID) {
			return {
				label: dealConstructionItem.name,
				value: `${MANUAL_INPUT_PREFIX}${dealConstructionItem.name}`
			};
		}

		return {
			label: dealConstructionItem.name,
			value: String(dealConstructionItem.id)
		};
	}, [dealConstructionItem]);

	const onInputChangeConstructionItem = useCallback(
		(text: string): void => {
			setConstructionItemOptions(getOptions(text));
		},
		[getOptions]
	);

	const onChangeConstructionItem = useCallback(
		(option: ComponentProps<typeof SingleComboBox>["value"]): void => {
			// 選択状態を解除した場合
			if (!option) {
				setValue("dealConstructionItem", null);
				setValue("unitAmount", 0);
				setValue("unitName", null);
				setConstructionItemOptions(getOptions(""));
				return;
			}

			// 「直接入力」オプションが選択された場合
			// dealConstructionItemには特殊なIDを使用して保存
			if (option.value.startsWith(MANUAL_INPUT_PREFIX)) {
				const customName = String(option.value).slice(
					MANUAL_INPUT_PREFIX.length
				);
				const manualInputItem: DealConstructionItem = {
					constructionItemId: MANUAL_INPUT_CONSTRUCTION_ID,
					excludeEstimation: true,
					id: MANUAL_INPUT_CONSTRUCTION_ID,
					name: customName,
					overridable: false,
					seq: 0,
					unit: "",
					unitAmount: 0
				};
				reset(
					buildResetConstruction(getValues("operationType"), manualInputItem),
					{ keepDefaultValues: true }
				);
				setConstructionItemOptions(getOptions(""));
				return;
			}

			// 元々選択肢として存在している施工項目が選択された場合
			const findItem = dealConstructionItems.find(
				(item: DealConstructionItem) => item.id === Number(option.value)
			)!;
			reset(buildResetConstruction(getValues("operationType"), findItem), {
				keepDefaultValues: true
			});
			setConstructionItemOptions(getOptions(""));
		},
		[dealConstructionItems, getOptions, getValues, reset, setValue]
	);

	const handleChangeRenteeChargePer = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			const { value } = e.target;
			if (Number.isNaN(Number(value))) return;
			setValue("renteeChargePer", value === "" ? "" : Number(value));
			if (Number(value) > 100) return;
			const ownerChargePer = 100 - Number(value) - Number(companyChargePer);
			setValue("ownerChargePer", ownerChargePer);
		},
		[companyChargePer, setValue]
	);
	const estimationType = watch("estimationType");
	const isProposalDisabled = dealConstructionItem?.excludeEstimation === false;
	return (
		<>
			<FormControl
				chip="必須"
				disabled={disabled}
				isInvalid={!!errors.dealConstructionItem}
				label="施工項目"
			>
				<SingleComboBox
					keepMenuOpenOnBlur
					maxMenuHeight={isPc ? "95dvh" : 300}
					menuPosition={isPc ? "right" : "bottom"}
					onChange={onChangeConstructionItem}
					onInputChange={onInputChangeConstructionItem}
					options={constructionItemOptions}
					placeholder="施工項目を検索"
					value={selectedConstructionItemValue}
					w="100%"
				/>
				<FormControlText
					error={errors.dealConstructionItem?.message}
					help="施工項目を検索できます。項目が存在しない場合は入力内容を使用できます。"
				/>
			</FormControl>
			{docType === "estimation" ? (
				<FormControl disabled={disabled} label="提案ラベル">
					<Flex gap={designTokens["spacing/s"]}>
						<InputRadio
							checked={estimationType === ESTIMATION_TYPE.NONE}
							label="なし"
							onChange={(): void => {
								setValue("estimationType", ESTIMATION_TYPE.NONE);
							}}
							value={ESTIMATION_TYPE.NONE}
						/>
						<InputRadio
							checked={estimationType === ESTIMATION_TYPE.PROPOSAL}
							disabled={disabled || isProposalDisabled}
							label="あり"
							onChange={(): void => {
								setValue("estimationType", ESTIMATION_TYPE.PROPOSAL);
							}}
							value={ESTIMATION_TYPE.PROPOSAL}
						/>
					</Flex>
				</FormControl>
			) : null}
			<FormControl
				disabled={disabled}
				isInvalid={!!errors.point}
				label="施工箇所"
			>
				<Input placeholder="例）リビング" {...register("point")} />
				<FormControlText error={errors.point?.message} />
			</FormControl>
			<FormRow>
				<FormControl
					chip="必須"
					disabled={disabled}
					isInvalid={!!errors.unitAmount}
					label="単価"
				>
					<Input placeholder="単価" type="number" {...register("unitAmount")} />
					<FormControlText error={errors.unitAmount?.message} />
				</FormControl>
				<FormControl
					chip="必須"
					disabled={disabled}
					isInvalid={!!errors.unit}
					label="数量"
				>
					<Input placeholder="数量" type="number" {...register("unit")} />
					<FormControlText error={errors.unit?.message} />
				</FormControl>
			</FormRow>
			<FormControl
				chip={isManualInput ? "必須" : undefined}
				disabled={disabled || !isManualInput}
				isInvalid={!!errors.unitName}
				label="単位"
			>
				{isManualInput ? (
					<>
						<Input
							maxLength={10}
							placeholder="例）式"
							{...register("unitName")}
						/>
						<FormControlText error={errors.unitName?.message} />
					</>
				) : (
					<Input disabled value={dealConstructionItem?.unit ?? ""} />
				)}
			</FormControl>
			<FormRow>
				<FormControl
					disabled={disabled}
					isInvalid={!!errors.renteeChargePer}
					label="借主負担%"
				>
					<Input
						placeholder="借主負担％"
						{...register("renteeChargePer")}
						onChange={handleChangeRenteeChargePer}
					/>
					<FormControlText error={errors.renteeChargePer?.message} />
				</FormControl>
				<FormControl
					disabled={disabled}
					isInvalid={!!errors.ownerChargePer}
					label="貸主負担%"
				>
					<Input placeholder="貸主負担％" {...register("ownerChargePer")} />
					<FormControlText error={errors.ownerChargePer?.message} />
				</FormControl>
				<FormControl
					disabled={disabled}
					isInvalid={!!errors.companyChargePer}
					label="管理負担%"
				>
					<Input placeholder="管理負担％" {...register("companyChargePer")} />
					<FormControlText error={errors.companyChargePer?.message} />
				</FormControl>
			</FormRow>
			<FormControl disabled={disabled} isInvalid={!!errors.note} label="備考">
				<InputText
					maxRows={8}
					placeholder="備考）その他連絡事項など"
					resize="autoRows"
					rows={2}
					{...register("note")}
				/>
			</FormControl>
		</>
	);
};
